// Pan and zoom for the system map canvas: one transformed layer, pointer-based
// drag (mouse, pen, one finger), two-finger pinch, wheel zoom around the
// cursor, and an eased fly-to. The transform is written straight to the layer
// in a requestAnimationFrame; a reactive copy (`view`) feeds whatever follows
// the camera (the chips beside a card).
//
// Limits come from the map: a scale range and a content box per level
// (setLimits). Zooming is clamped to the range and panning to the box, so the
// map can never be thrown off screen. Zooming on past a limit, by wheel,
// pinch, button or key, reports an overscroll so the map can change level: one
// level per burst of input (createZoomStepper latches after a step until the
// burst has gone quiet), so a fast pinch-out can never run past the all level
// (R53 H1). `animating` is true while an eased fly is running, so what follows
// the camera (the chips tray) can wait for it to land (R53 M5).
import {onBeforeUnmount, reactive, ref} from 'vue';
import {clampScale, clampTranslate, createZoomStepper, overscrollAction} from '~/utils/systemMapLayout';

export function useSystemMapViewport({canvasEl, layerEl, onOverscroll = null, onPointer = null} = {}) {
  const view = reactive({x: 0, y: 0, s: 1});
  const size = reactive({w: 1440, h: 800});
  const dragging = ref(false);
  const animating = ref(false);
  const reducedMotion = ref(false);

  let tf = {x: 0, y: 0, s: 1};
  let frame = 0;
  let anim = 0;
  const pointers = new Map();
  let pinchStart = null;
  let dragStart = null;
  let moved = false;

  // Limits (set by the map per level)
  let level = 'overview';
  let range = {min: 0.02, max: 8};
  let bounds = null;
  let inset = {top: 0, bottom: 0};
  const stepper = createZoomStepper();

  const apply = () => {
    frame = 0;
    const el = layerEl.value;
    if (el) el.style.transform = `translate3d(${tf.x}px, ${tf.y}px, 0) scale(${tf.s})`;
    view.x = tf.x;
    view.y = tf.y;
    view.s = tf.s;
  };
  const schedule = () => {
    if (!frame) frame = requestAnimationFrame(apply);
  };

  const setLimits = (next) => {
    if (next.level) level = next.level;
    if (next.range) range = next.range;
    if ('bounds' in next) bounds = next.bounds;
    if (next.inset) inset = next.inset;
    stepper.setLevel(level);
  };

  const clampAll = (t) => {
    const s = clampScale(t.s, range);
    const k = s / t.s;
    const scaled = {x: t.x, y: t.y, s};
    if (k !== 1) {
      // Keep the centre of the canvas where it was when the scale was clamped
      const cx = size.w / 2, cy = size.h / 2;
      scaled.x = cx - (cx - t.x) * k;
      scaled.y = cy - (cy - t.y) * k;
    }
    return bounds ? clampTranslate(scaled, bounds, size, {inset}) : scaled;
  };

  const setView = (next, {animate = false, ms = 460, clamp = true} = {}) => {
    cancelAnimationFrame(anim);
    anim = 0;
    const target = clamp ? clampAll({x: next.x, y: next.y, s: next.s}) : {x: next.x, y: next.y, s: next.s};
    if (!animate || reducedMotion.value) {
      tf = target;
      animating.value = false;
      schedule();
      return;
    }
    const from = {...tf};
    const t0 = performance.now();
    animating.value = true;
    const step = (now) => {
      const t = Math.min(1, (now - t0) / ms);
      const e = t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
      tf = {x: from.x + (target.x - from.x) * e, y: from.y + (target.y - from.y) * e, s: from.s + (target.s - from.s) * e};
      apply();
      if (t < 1) anim = requestAnimationFrame(step);
      else { anim = 0; animating.value = false; }
    };
    anim = requestAnimationFrame(step);
  };

  /**
   * Zoom by a factor around a point in canvas pixels. A `discrete` input (the
   * zoom buttons, the + and - keys) at the level's floor or ceiling changes
   * level at once; a wheel notch adds up towards the overscroll limit.
   */
  const zoomAt = (factor, cx = size.w / 2, cy = size.h / 2, animate = false, {discrete = false} = {}) => {
    const atMin = tf.s <= range.min + 1e-6, atMax = tf.s >= range.max - 1e-6;
    const requested = tf.s * factor;
    const s = clampScale(requested, range);
    const k = s / tf.s;
    setView({x: cx - (cx - tf.x) * k, y: cy - (cy - tf.y) * k, s}, {animate, ms: 220});
    const action = stepper.zoom({requested, clamped: s, factor, discrete, atMin, atMax});
    if (action) onOverscroll?.(action, {x: cx, y: cy});
  };

  const toMap = (cx, cy) => ({x: (cx - tf.x) / tf.s, y: (cy - tf.y) / tf.s});
  const toScreen = (mx, my) => ({x: mx * tf.s + tf.x, y: my * tf.s + tf.y});

  const measure = () => {
    const el = canvasEl.value;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (r.width && r.height) {
      size.w = r.width;
      size.h = r.height;
    }
  };

  const local = (e) => {
    const r = canvasEl.value.getBoundingClientRect();
    return {x: e.clientX - r.left, y: e.clientY - r.top};
  };

  let pinchOver = 1;
  let pinchMid = null;

  const onPointerDown = (e) => {
    if (e.button !== undefined && e.button !== 0) return;
    pointers.set(e.pointerId, local(e));
    moved = false;
    if (pointers.size === 1) {
      dragStart = {px: e.clientX, py: e.clientY, x: tf.x, y: tf.y};
    } else if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchStart = {dist: Math.hypot(a.x - b.x, a.y - b.y), s: tf.s, mid: {x: (a.x + b.x) / 2, y: (a.y + b.y) / 2}, x: tf.x, y: tf.y};
      pinchOver = 1;
      dragStart = null;
    }
  };

  const onPointerMove = (e) => {
    if (e.pointerType !== 'touch') onPointer?.(local(e));
    if (!pointers.has(e.pointerId)) return;
    pointers.set(e.pointerId, local(e));
    if (pointers.size === 2 && pinchStart) {
      const [a, b] = [...pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      const mid = {x: (a.x + b.x) / 2, y: (a.y + b.y) / 2};
      const requested = pinchStart.s * (dist / Math.max(1, pinchStart.dist));
      const s = clampScale(requested, range);
      pinchOver = requested / s;
      pinchMid = mid;
      const k = s / pinchStart.s;
      tf = clampAll({
        x: mid.x - (pinchStart.mid.x - pinchStart.x) * k,
        y: mid.y - (pinchStart.mid.y - pinchStart.y) * k,
        s,
      });
      moved = true;
      dragging.value = true;
      schedule();
      return;
    }
    if (pointers.size === 1 && dragStart) {
      const dx = e.clientX - dragStart.px, dy = e.clientY - dragStart.py;
      if (!moved && Math.hypot(dx, dy) < 5) return;
      if (!moved) {
        moved = true;
        dragging.value = true;
        try { canvasEl.value.setPointerCapture(e.pointerId); } catch (err) { /* older browsers */ }
      }
      cancelAnimationFrame(anim);
      tf = clampAll({x: dragStart.x + dx, y: dragStart.y + dy, s: tf.s});
      schedule();
    }
  };

  const onPointerUp = (e) => {
    pointers.delete(e.pointerId);
    try { canvasEl.value?.releasePointerCapture(e.pointerId); } catch (err) { /* not captured */ }
    if (pointers.size < 2 && pinchStart) {
      pinchStart = null;
      // A pinch that pushed well past the limit changes level once it ends (one level per pinch, however far it went)
      const action = stepper.offer(overscrollAction(level, pinchOver));
      pinchOver = 1;
      if (action) onOverscroll?.(action, pinchMid || {x: size.w / 2, y: size.h / 2});
    }
    if (pointers.size === 1) {
      const p = [...pointers.values()][0];
      const r = canvasEl.value.getBoundingClientRect();
      dragStart = {px: p.x + r.left, py: p.y + r.top, x: tf.x, y: tf.y};
    }
    if (pointers.size === 0) {
      dragStart = null;
      // Let the click that follows a real drag be ignored by the cards
      if (moved) setTimeout(() => { dragging.value = false; }, 0);
      else dragging.value = false;
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    const p = local(e);
    // Gentler steps than a raw notch: about 8 notches from fit to the limit
    const factor = Math.exp(-e.deltaY * (e.deltaMode === 1 ? 0.04 : 0.0012));
    zoomAt(factor, p.x, p.y);
  };

  let ro = null;
  const attach = () => {
    const el = canvasEl.value;
    if (!el) return;
    try {
      reducedMotion.value = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    } catch (err) { reducedMotion.value = false; }
    measure();
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    el.addEventListener('wheel', onWheel, {passive: false});
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => measure());
      ro.observe(el);
    }
  };
  const detach = () => {
    const el = canvasEl.value;
    if (el) {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
      el.removeEventListener('wheel', onWheel);
    }
    ro?.disconnect();
    cancelAnimationFrame(frame);
    cancelAnimationFrame(anim);
  };
  onBeforeUnmount(detach);

  /** The current transform, un-reactive (for maths in event handlers). */
  const current = () => ({...tf});

  return {view, size, dragging, animating, reducedMotion, setView, setLimits, zoomAt, toMap, toScreen, measure, attach, detach, current};
}
